/**
 * Calculator parameters — single source of truth for every downstream module
 * (formula, storage, form, results). SAD §8 ID/versioning convention: keeping
 * every tunable number here makes a trainer parameter change a one-file edit.
 *
 * NOTE (SAD §11 High risk): the activity multipliers, per-goal macro splits and
 * the safe-minimum floor are spec §8 DEFAULTS used as placeholders until Світлана
 * Мамченко signs off (tracked in T8). Adjust the numbers below only.
 */

/**
 * Bumped BY HAND whenever the formula, input bounds or macro rules change. A saved
 * calculation stamped with a different version is treated as stale and not restored
 * (AC-09). Integer, not the site release tag.
 */
export const CALCULATOR_VERSION = 1 as const

/** Allowed input ranges (AC-02). Not clinical eligibility criteria. */
export const INPUT_BOUNDS = {
  age: { min: 16, max: 80, unit: 'років' },
  height: { min: 120, max: 220, unit: 'см' },
  weight: { min: 35, max: 200, unit: 'кг' },
} as const

export type InputField = keyof typeof INPUT_BOUNDS

/**
 * Four home-movement presets (CONTEXT glossary — not gym-centric athletic tiers).
 * Multipliers are the standard Mifflin-St Jeor activity factors (placeholder).
 */
export const ACTIVITY_PRESETS = [
  {
    id: 'sedentary',
    label: 'Малорухливий спосіб життя',
    hint: 'майже без тренувань',
    multiplier: 1.2,
  },
  {
    id: 'light',
    label: 'Легка активність',
    hint: '1–2 домашні тренування на тиждень',
    multiplier: 1.375,
  },
  {
    id: 'moderate',
    label: 'Помірна активність',
    hint: '3–4 тренування на тиждень',
    multiplier: 1.55,
  },
  {
    id: 'active',
    label: 'Висока активність',
    hint: '5+ тренувань на тиждень',
    multiplier: 1.725,
  },
] as const

export type ActivityLevel = (typeof ACTIVITY_PRESETS)[number]['id']

/**
 * Nutrition goals reframed around energy, strength and gentle adjustment — never
 * deficit / diet / weight-loss framing (AC-04, CONTEXT invariant).
 * `energyFactor` scales TDEE; `macros` is the protein/fat/carb split as fractions
 * of energy (must sum to 1). Placeholder split pending trainer sign-off.
 */
export const NUTRITION_GOALS = [
  {
    id: 'maintain',
    label: 'Підтримати енергію',
    hint: 'зберегти форму та самопочуття',
    energyFactor: 1.0,
    macros: { protein: 0.3, fat: 0.3, carb: 0.4 },
  },
  {
    id: 'strength',
    label: 'Стати сильнішою',
    hint: 'більше сил для тренувань',
    energyFactor: 1.1,
    macros: { protein: 0.35, fat: 0.25, carb: 0.4 },
  },
  {
    id: 'gentle',
    label: 'М’яке коригування',
    hint: 'плавні зміни у своєму темпі',
    energyFactor: 0.9,
    macros: { protein: 0.35, fat: 0.3, carb: 0.35 },
  },
] as const

export type NutritionGoal = (typeof NUTRITION_GOALS)[number]['id']

/**
 * Orientational lower bound for displayed energy (AC-04). When the computed estimate
 * falls below this, the display is clamped and labelled a lower bound — never a diet
 * target. Placeholder value (kcal) pending trainer sign-off.
 */
export const SAFE_MINIMUM_FLOOR = 1200 as const

/** kcal per gram, used to convert the energy split into macro grams. */
export const KCAL_PER_GRAM = { protein: 4, fat: 9, carb: 4 } as const
